import React, { useEffect } from "react";
import {
  BlockHeroCompact,
  BlockSection,
  BlockText,
  BlockFigure,
  RecipeRow,
  RecipeCell,
  RecipeBoard,
  projectPageShellClass,
  projectPageBottomClass,
  blockSectionBodyWrapClass,
} from "../../designSystem/projectBlocks";

const BASE = "/projects/GENx Facade Solution/images";

export default function GENxFacadeSolutionPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main className="min-h-screen bg-white dark:bg-black text-black dark:text-white transition-colors duration-500">
      <div className={`${projectPageShellClass} pt-[72px] ${projectPageBottomClass} box-border`}>
        <BlockHeroCompact
          title="GENx Facade Solution"
          description="An algorithm that solves the non-typical regions of automated facade systems — analysing each panel’s local geometric context and instantiating the right mullion types to match angles, neighbours, and architectural intent."
          meta={["New York, USA", "GENx Design and Technology", "Catia (3DExperience)", "EKL", "2022"]}
        />

        <RecipeBoard>
          <RecipeRow>
            <RecipeCell span={12} className="!p-0 !border-r-0">
              <BlockFigure
                src={`${BASE}/combined.jpg`}
                alt="GENx Facade Solution — non-typical regions overview"
                ratio="banner"
                fit="cover"
              />
            </RecipeCell>
          </RecipeRow>
        </RecipeBoard>

        <BlockSection title="Project scope">
          <BlockText
            className={blockSectionBodyWrapClass}
            paragraphs={[
              "At GENx the goal is full automation of design processes. The main obstacle in automating facade systems is the irregular regions where the general rules do not apply — usually the result of a deliberate architectural choice for aesthetic or functional reasons. Automating those regions means analysing each panel locally, with awareness of the surrounding geometry and architectural elements, then solving the mullions to match.",
              "I developed an algorithm that performs that local analysis and generates appropriate mullion geometries — taking into account the angle conditions and mullion types at each neighbour of the panel being processed.",
            ]}
          />
        </BlockSection>

        <BlockSection title="Technical approach">
          <BlockText
            className={blockSectionBodyWrapClass}
            paragraphs={[
              "The tool ships in two versions sharing the same algorithm. The first plugs into an automated facade fabrication pipeline — used in the Four Seasons Dubai project for fabrication-document generation. The second runs in feasibility mode to check whether the concept-design’s primary geometries will satisfy the criteria before committing to full development.",
              "Written in EKL, the algorithm is composed of two sub-algorithms. The first detects, for each mullion, its type and angle condition; the second consumes that detection and instantiates the appropriate mullion types — structuring the parts so the output can merge cleanly into the rest of the facade data.",
            ]}
          />

          <RecipeBoard>
            <RecipeRow>
              <RecipeCell span={6} className="!p-0">
                <BlockFigure src={`${BASE}/Non typical 1.png`} alt="Non-typical region 1" ratio="classic" fit="contain" caption="Region · 01" />
              </RecipeCell>
              <RecipeCell span={6} className="!p-0 !border-r-0">
                <BlockFigure src={`${BASE}/Non typical 3.png`} alt="Non-typical region 3" ratio="classic" fit="contain" caption="Region · 02" />
              </RecipeCell>
            </RecipeRow>
            <RecipeRow>
              <RecipeCell span={6} className="!p-0">
                <BlockFigure src={`${BASE}/Non typical 4.png`} alt="Non-typical region 4" ratio="classic" fit="contain" caption="Region · 03" />
              </RecipeCell>
              <RecipeCell span={6} className="!p-0 !border-r-0">
                <BlockFigure src={`${BASE}/Non typical 11.png`} alt="Non-typical region 11" ratio="classic" fit="contain" caption="Region · 04" />
              </RecipeCell>
            </RecipeRow>
          </RecipeBoard>
        </BlockSection>

        <BlockSection title="Process documentation">
          <BlockText
            className={blockSectionBodyWrapClass}
            paragraphs={[
              "Extracts from the project portfolio documenting detection logic, mullion instantiation rules, and edge-case handling.",
            ]}
          />

          <RecipeBoard>
            <RecipeRow>
              <RecipeCell span={12} className="!p-0 !border-r-0">
                <BlockFigure src={`${BASE}/portfolio pages7.png`} alt="Process documentation — page 1" ratio="banner" fit="contain" invert />
              </RecipeCell>
            </RecipeRow>
            <RecipeRow>
              <RecipeCell span={12} className="!p-0 !border-r-0">
                <BlockFigure src={`${BASE}/portfolio pages8.png`} alt="Process documentation — page 2" ratio="banner" fit="contain" invert />
              </RecipeCell>
            </RecipeRow>
          </RecipeBoard>
        </BlockSection>
      </div>
    </main>
  );
}
